import { ScrollViewNoteProps as NoteViewProps } from '@/routes/tickets/TicketEditor.types'
import { Providers } from '@microsoft/mgt-react'
import { GraphError } from '@microsoft/microsoft-graph-client'
import { Button } from 'devextreme-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import './NoteView.scss'
import { getTicketNoteAuthorLabel } from '@/routes/tickets/notes/ticketNotes'

const noteAttachmentsPresenceCache = new Map<string, boolean>()
const noteAttachmentsPendingRequests = new Map<string, Promise<boolean>>()

const NoteView = (props: NoteViewProps) => {
  const { note, readOnly, visibleEditClick, onEditClick } = props
  const data: Date = new Date(note.data ?? '')
  const [existAttachments, setExistAttachments] = useState<boolean>(false)
  const [isVisible, setIsVisible] = useState(false)
  const noteContentRef = useRef<HTMLDivElement>(null)

  const displayName = getTicketNoteAuthorLabel(note)

  const getDirectionStatus = () => {
    switch (note.direction) {
      case 'uscita':
        return { className: 'emailSend', hint: 'Nota inviata', label: 'Inviata' }
      case 'ingresso':
        return { className: 'emailReceived', hint: 'Nota ricevuta', label: 'Ricevuta' }
      default:
        return null
    }
  }

  const directionStatus = getDirectionStatus()

  useEffect(() => {
    const currentElement = noteContentRef.current
    if (!currentElement) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      {
        rootMargin: '200px 0px',
      },
    )

    observer.observe(currentElement)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!isVisible) {
      return
    }

    if (!note.ticket?.attachmentsFolderUrl || !note.tag) {
      setExistAttachments(false)
      return
    }

    const cacheKey = `${note.ticket.attachmentsFolderUrl}|${note.tag}`
    const cachedValue = noteAttachmentsPresenceCache.get(cacheKey)
    if (cachedValue != null) {
      setExistAttachments(cachedValue)
      return
    }

    let cancelled = false

    const ensureRequest = () => {
      const existingRequest = noteAttachmentsPendingRequests.get(cacheKey)
      if (existingRequest) return existingRequest

      const newRequest = Providers.globalProvider.graph
        .api(
          `drives/${import.meta.env.VITE_QSADMINSITE_DRIVE_ID}/root:/${note.ticket?.attachmentsFolderUrl}/_notes/${note?.tag}/attachments:/children`,
        )
        .get()
        .then((value) => (value.value && value.value.length > 0 ? true : false))
        .catch((error: GraphError) => {
          if (error?.statusCode === 404) return false
          throw error
        })
        .finally(() => {
          noteAttachmentsPendingRequests.delete(cacheKey)
        })

      noteAttachmentsPendingRequests.set(cacheKey, newRequest)
      return newRequest
    }

    ensureRequest()
      .then((existsAttachments) => {
        noteAttachmentsPresenceCache.set(cacheKey, existsAttachments)
        if (!cancelled) setExistAttachments(existsAttachments)
      })
      .catch((error: GraphError) => {
        if (cancelled) return
        setExistAttachments(false)
        console.log('errore recupero allegati nota', error)
      })

    return () => {
      cancelled = true
    }
  }, [isVisible, note.tag, note.ticket?.attachmentsFolderUrl])

  const noteHtml = useMemo(() => {
    if (!note.testo) return ''
    if (typeof DOMParser === 'undefined') return note.testo

    const htmlDocument = new DOMParser().parseFromString(note.testo, 'text/html')
    const images = htmlDocument.body.querySelectorAll('img')

    images.forEach((imageElement) => {
      const source = imageElement.getAttribute('src')
      imageElement.setAttribute('loading', 'lazy')
      imageElement.setAttribute('decoding', 'async')

      if (!isVisible && source) {
        imageElement.setAttribute('data-note-src', source)
        imageElement.removeAttribute('src')
      } else if (isVisible) {
        const deferredSource = imageElement.getAttribute('data-note-src')
        if (!source && deferredSource) {
          imageElement.setAttribute('src', deferredSource)
        }
        imageElement.removeAttribute('data-note-src')
      }
    })

    return htmlDocument.body.innerHTML
  }, [isVisible, note.testo])

  return (
    <div className="dx-theme-background-color note-view-container">
      <div className="note-view-card">
        <div className="note-view-header">
          <div className="note-view-header-left">
            <div className="note-view-author">{displayName}</div>
            <div className="note-view-date">
              {data.toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}{' '}
              •{' '}
              {data.toLocaleTimeString('it-IT', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </div>
          </div>
          <div className="note-view-badges">
            {directionStatus && (
              <div className={`note-view-badge badge-email ${directionStatus.className}`} title={directionStatus.hint}>
                <i className="dx-icon-email" />
                <span>{directionStatus.label}</span>
              </div>
            )}
            {existAttachments && (
              <div className="note-view-badge badge-attachment" title="Allegati presenti">
                <i className="dx-icon-attach" />
              </div>
            )}
            {note.chiusura && (
              <div className="note-view-badge badge-closure" title="Nota di chiusura">
                <i className="dx-icon-check" />
                <span>Chiusura</span>
              </div>
            )}
          </div>
        </div>

        <div
          ref={noteContentRef}
          className="note-view-content note-view-html-content"
          dangerouslySetInnerHTML={{ __html: noteHtml }}
        />

        {(visibleEditClick ?? true) && (
          <div className="note-view-footer">
            <Button
              icon={readOnly ? 'eyeopen' : 'edit'}
              text={readOnly ? 'Visualizza' : 'Modifica'}
              stylingMode="text"
              onClick={onEditClick}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default NoteView
